
import { computed, defineComponent, ref, PropType } from 'vue';
import useNumbers from '@/composables/useNumbers';
import useTailwind from '@/composables/useTailwind';
import { format, fromUnixTime } from 'date-fns';
import numeral from 'numeral';
import BalCard from '@/components/_global/BalCard/BalCard.vue';
import BalBlankSlate from '@/components/_global/BalBlankSlate/BalBlankSlate.vue';
import BalIcon from '@/components/_global/BalIcon/BalIcon.vue';
import { GqlBalancerPoolSnapshot } from '@/embr/services/embr/embr-types';
import BalBtn from '@/components/_global/BalBtn/BalBtn.vue';
import { capitalize } from 'lodash';
import VueApexCharts from 'vue3-apexcharts';

export default defineComponent({
  name: 'PoolVolumeChart',
  props: {
    snapshots: {
      type: Array as PropType<GqlBalancerPoolSnapshot[]>
    }
  },

  components: {
    VueApexCharts
  },

  setup(props) {
    type ChartType = 'volume' | 'tvl' | 'fees';
    const currentChartType = ref<ChartType>('volume');
    const series = ref<any>([]);

    const chartLabel = computed(() => {
      switch (currentChartType.value) {
        case 'volume':
          return 'Volume';
        case 'fees':
          return 'Fees';
        case 'tvl':
          return 'TVL';
      }
    });

    const chartOptions = {
      grid: {
        show: true,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      chart: {
        toolbar: {
          show: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: 1
      },
      colors: ['#FFFFFF'],
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: ['rgba(255, 255, 255, 0)'],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.2
        }
      },
      yaxis: {
        show: false
      },
      xaxis: {
        type: 'category',
        categories: props.snapshots?.map(snapshot => format(fromUnixTime(snapshot.timestamp), 'MMM. d')),
        axisTick: {
          alignWithLabel: false
        }
      },
      markers: {
        size: 0
      },
      tooltip: {
        x: {
          format: 'dd/MM/yy HH:mm'
        },
        y: {
          formatter: function(val) {
            return parseFloat(val).toFixed(2);
          }
        },
        theme: 'dark'
      },
      legend: {
        show: false
      }
    };

    series.value = [
      {
        name: chartLabel.value,
        data: props.snapshots?.map(snapshot => {
          switch (currentChartType.value) {
            case 'volume':
              return parseFloat(snapshot.swapVolume24h);
            case 'fees':
              return parseFloat(snapshot.swapFees24h);
            case 'tvl':
              return parseFloat(snapshot.totalLiquidity);
          }
        })
      }
    ];

    function setCurrentChart(chartType: ChartType) {
      currentChartType.value = chartType;
      series.value = [
        {
          name: chartLabel.value,
          data: props.snapshots?.map(snapshot => {
            switch (currentChartType.value) {
              case 'volume':
                return parseFloat(snapshot.swapVolume24h);
              case 'fees':
                return parseFloat(snapshot.swapFees24h);
              case 'tvl':
                return parseFloat(snapshot.totalLiquidity);
            }
          })
        }
      ];
    }

    return {
      setCurrentChart,
      chartLabel,
      currentChartType,
      series,
      chartOptions
    };
  }
});
